import { ChangeDetectionStrategy, Component, computed, inject, signal, OnInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InfoWidgetComponent } from '@/shared/info-widget/info-widget.component';
import { InfoBargraphComponent } from '@/shared/info-bargraph/info-bargraph.component';
import moment from 'moment';
import { DeviceContextService, DeviceDetails } from '../device-context.service';
import { SystemMonitorChartComponent } from '@/shared/system-monitor-chart/system-monitor-chart.component';
import { SystemMonitorSparkComponent } from '@/shared/system-monitor-chart/system-monitor-spark.component';
import { CloudApiService, ResolvedGeo } from '../../../services/cloud-api.service';
import { formatCoordinates, geoSourceLabel, googleMapsUrl } from '@/cloud/models/geo';
import { deviceDisplayName } from '@/cloud/services/device-display';
import { SystemMonitorStat } from 'src/api/system-monitor-stat';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { BadgeModule } from 'primeng/badge';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { parseMessages } from 'src/api/messages';
import { MessagesPanelComponent } from '@/shared/messages-panel/messages-panel.component';
import { RouterLink } from '@angular/router';
import { GeoLocationDialogComponent } from '@/cloud/components/geo-location-dialog/geo-location-dialog.component';
import { PingLinkComponent } from '@/shared/ping-link/ping-link.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { LocationDto } from '@/cloud/models/tree.models';
import { deriveDeviceStatus } from '@/cloud/models/device-status';

// DeviceDetails now sourced from DeviceContextService

@Component({
    selector: 'app-device-overview',
    standalone: true,
    imports: [
        CommonModule,
        InfoWidgetComponent,
        InfoBargraphComponent,
        SystemMonitorChartComponent,
        SystemMonitorSparkComponent,
        ButtonModule,
        SelectButtonModule,
        FormsModule,
        BadgeModule,
        DialogModule,
        ProgressSpinnerModule,
        MessageModule,
        MessagesPanelComponent,
        RouterLink,
        GeoLocationDialogComponent,
        PingLinkComponent,
        EmptyStateComponent,
        LoadingSkeletonComponent
    ],
    templateUrl: './device-overview.html',
    styleUrl: './device-overview.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceOverview implements OnInit, OnDestroy {
    /** Template helper — see deviceDisplayName for the precedence. */
    displayName = deviceDisplayName;

    private ctx = inject(DeviceContextService);
    private api = inject(CloudApiService);
    deviceSig = this.ctx.device; // signal<DeviceDetails|null>
    private now = signal(Date.now());
    private tick = setInterval(() => this.now.set(Date.now()), 1000);

    private namespaceId = computed(() => this.deviceSig()?.appNamespaceId);
    private roomsEffect = effect(() => {
        const namespaceId = this.namespaceId();
        if (namespaceId) this.api.refreshNamespaceLocations(namespaceId);
    });
    roomsLoading = computed(() => this.api.locations() === null);
    processorRooms = computed(() => {
        const device = this.deviceSig();
        if (!device?.appNamespaceId) return [];
        const locations: LocationDto[] = this.api.locations() ?? [];
        const inTenant = locations.filter(location => location.appNamespaceId === device.appNamespaceId);
        const byId = new Map(inTenant.map(location => [location.id, location]));
        return inTenant
            .filter(location => location.type === 'room' && location.controlSystemDeviceId === device.id)
            .map(location => ({
                ...location,
                parentName: location.parentId ? byId.get(location.parentId)?.name : undefined
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    });
    connectionStatus = computed(() => deriveDeviceStatus(this.deviceSig(), this.now()));
    overviewHealth = computed(() => {
        const status = this.connectionStatus();
        if (status.key !== 'online') {
            return { title: status.label, detail: this.p ? 'Showing the last reported system data.' : 'Waiting for system telemetry.', icon: status.icon, tone: 'is-waiting' };
        }
        if (!this.p) {
            return { title: 'Waiting for status', detail: 'System details appear after the first telemetry update.', icon: 'pi pi-clock', tone: 'is-waiting' };
        }
        return {
            title: this.healthText(),
            detail: this.healthSubtext(),
            icon: this.errorCount() ? 'pi pi-times-circle' : this.warningCount() ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle',
            tone: this.errorCount() ? 'has-errors' : this.warningCount() ? 'has-warning' : ''
        };
    });

    // Tunnel connection state (fetched once on init, then updated via real-time snapshots)
    tunnelConnected = signal<boolean | null>(null); // null = unknown/loading

    // Geo location dialog state
    geoDialogVisible = signal(false);

    /** Display location, resolved by the API: manual override > room or a parent location > IP estimate. */
    resolvedGeo = computed(() => this.deviceSig()?.resolvedGeo ?? null);

    /**
     * Set when the cloud could not store this device's last check-in as sent. The device keeps
     * checking in and looks online, so without this banner the only symptom is telemetry that
     * quietly stops changing.
     */
    checkInHealth = computed(() => this.deviceSig()?.checkInHealth ?? null);

    /** True for the harder case: the write was rejected and the stored payload is stale. */
    checkInWriteFailed = computed(() => this.checkInHealth()?.state === 'WriteFailed');

    checkInHealthTitle = computed(() => {
        switch (this.checkInHealth()?.state) {
            case 'WriteFailed':
                return 'This device\u2019s latest data could not be saved';
            case 'Truncated':
                return 'This device reports more data than we can store';
            default:
                return 'Some of this device\u2019s data had to be altered to save it';
        }
    });

    /** What this means for what the page is showing, and what to do about it. */
    checkInHealthSummary = computed(() => {
        switch (this.checkInHealth()?.state) {
            case 'WriteFailed':
                return 'Live status is current, but the telemetry below is from an earlier check-in. This usually means the device is reporting a name the cloud cannot store \u2014 a room or signal containing a "." for instance.';
            case 'Truncated':
                // Deliberately explicit that the sections are missing rather than old: they are
                // left out precisely so nothing here can look current when it is not.
                return 'Everything shown below is current, but the sections listed are not stored at all \u2014 they are missing rather than out of date. The processor is sending more than the size budget allows.';
            default:
                return 'The data is stored, but under a name the device did not report. Rename it on the processor to keep the two in step.';
        }
    });

    /** How long it has been happening — a one-off and a fortnight of failures read differently. */
    checkInHealthOccurrenceNote = computed(() => {
        const health = this.checkInHealth();
        const occurrences = health?.occurrences ?? 0;
        if (!health || occurrences <= 1 || !health.firstSeenUtc) return '';
        return `Seen on ${occurrences} check-ins since ${moment(health.firstSeenUtc).fromNow()}.`;
    });

    geoSourceLabel = computed(() => (this.resolvedGeo() ? geoSourceLabel(this.resolvedGeo()!) : null));

    /** The address when there is one, else the coordinates. */
    geoPlace(geo: ResolvedGeo): string {
        return geo.address?.trim() || formatCoordinates(geo.latitude, geo.longitude);
    }

    mapsUrl(geo: ResolvedGeo): string {
        return googleMapsUrl(geo.latitude, geo.longitude);
    }

    openGeoDialog(): void {
        this.geoDialogVisible.set(true);
    }

    // Local dashboard dialog state
    showLocalDashboardDialog = signal(false);
    localDashboardReachable = signal<boolean | null>(null); // null = checking, true = reachable, false = not reachable
    checkingReachability = signal(false);

    // Computed: local dashboard URL from payload
    localDashboardUrl = computed(() => {
        const url = this.device?.payload?.localDashboardUrl;
        return typeof url === 'string' && url.length > 0 ? url : null;
    });

    // Open the local dashboard dialog and check reachability
    openLocalDashboardDialog(): void {
        const url = this.localDashboardUrl();
        if (!url) return;

        this.showLocalDashboardDialog.set(true);
        this.localDashboardReachable.set(null);
        this.checkingReachability.set(true);

        // Try to check if the URL is reachable
        this.checkUrlReachability(url);
    }

    // Check if URL is reachable (best effort - may fail due to CORS)
    private async checkUrlReachability(url: string): Promise<void> {
        try {
            // Use a simple fetch with a short timeout
            // Note: This may fail due to CORS, but that's expected for local URLs
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);

            const response = await fetch(url, {
                method: 'HEAD',
                mode: 'no-cors', // This allows the request but we can't read the response
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            // With no-cors mode, we get an opaque response
            // If we get here without an error, the server likely responded
            this.localDashboardReachable.set(true);
        } catch (error: any) {
            // Check if this is a timeout/abort error (truly unreachable)
            if (error?.name === 'AbortError') {
                this.localDashboardReachable.set(false);
            } else {
                // Other errors (like cert errors, CORS, TypeError) usually mean
                // the server IS responding but browser blocked it - treat as reachable
                this.localDashboardReachable.set(true);
            }
        } finally {
            this.checkingReachability.set(false);
        }
    }

    // Proceed to local dashboard
    proceedToLocalDashboard(): void {
        const url = this.localDashboardUrl();
        if (url) {
            window.open(url, '_blank');
        }
        this.showLocalDashboardDialog.set(false);
    }

    // System monitor stats state
    stats = signal<SystemMonitorStat[]>([]);
    windowHours = signal<number>(1); // 1, 24, 168
    statsLoading = signal(false);
    private lastStatsRequestId = 0;
    private pollingTimer: any = null;
    systemMonitorOptions = [
        { label: '1 Hour', value: 1 },
        { label: '1 Day', value: 24 },
        { label: '7 Days', value: 168 }
    ];
    readonly windowLabel = computed(() => (this.windowHours() === 24 ? 'last day' : this.windowHours() === 168 ? 'last 7 days' : 'last hour'));

    // The full chart is opt-in (#261): a sparkline tile sits with the other performance
    // tiles, and the big chart opens below them. The choice is remembered per browser.
    readonly monitorExpanded = signal(readMonitorExpanded());
    toggleMonitor(): void {
        const next = !this.monitorExpanded();
        this.monitorExpanded.set(next);
        try {
            localStorage.setItem(MONITOR_EXPANDED_KEY, next ? '1' : '0');
        } catch {
            // Storage can be unavailable (private mode); the toggle still works for this visit.
        }
    }

    // Fetch stats whenever device changes (and on longTerm toggle)
    private statsEffect = effect(() => {
        const dev = this.deviceSig();
        const hours = this.windowHours();
        const id = dev?.id;
        if (!id) {
            this.stats.set([]);
            this.clearPolling();
            return;
        }
        this.loadInitialStats(id, hours);
        this.setupPolling(id, hours);
    });

    // Track tunnel status from real-time device snapshots
    private tunnelEffect = effect(() => {
        const dev = this.deviceSig();
        if (dev && (dev as any).tunnelConnected !== undefined) {
            this.tunnelConnected.set(!!(dev as any).tunnelConnected);
        }
    });

    ngOnInit(): void {
        this.checkTunnelStatus();
    }

    private async checkTunnelStatus(): Promise<void> {
        const devId = this.deviceSig()?.id;
        if (!devId) return;
        try {
            const status = await this.api.getDeviceTunnelStatus(devId);
            this.tunnelConnected.set(status.tunnelConnected);
        } catch {
            this.tunnelConnected.set(false);
        }
    }

    get windowHoursMode() {
        return this.windowHours();
    }
    set windowHoursMode(v: number) {
        const val = v === 1 || v === 24 || v === 168 ? v : 1;
        this.windowHours.set(val);
        this.stats.set([]); // clear for immediate visual feedback
    }

    private loadInitialStats(id: string, hours: number) {
        const requestId = ++this.lastStatsRequestId;
        this.statsLoading.set(true);
        const to = new Date();
        const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
        // Adjust limits to sample data appropriately for each time window:
        // 1 hour: 500 points (~1 every 7 seconds) - detailed short-term view
        // 1 day: 300 points (~1 every 5 minutes) - adequate for daily trends
        // 7 days: 336 points (~1 every 30 minutes) - adequate for weekly trends
        this.api
            .getDeviceStatsRange(id, from, to, hours === 1 ? 500 : hours === 24 ? 300 : 336)
            .then((resp) => {
                if (requestId !== this.lastStatsRequestId) return;
                const asc = [...resp.items].sort((a, b) => a.timestampUtc.localeCompare(b.timestampUtc));
                this.stats.set(asc);
            })
            .catch((err) => {
                if (requestId !== this.lastStatsRequestId) return;
                console.warn('DeviceOverview: failed initial stats load', err);
                this.stats.set([]);
            })
            .finally(() => {
                if (requestId === this.lastStatsRequestId) this.statsLoading.set(false);
            });
    }

    private setupPolling(id: string, hours: number) {
        this.clearPolling();
        // Poll interval based on window size. The API stores one sample per device every 5 minutes,
        // so polling the short window faster than once a minute only re-reads unchanged data.
        const intervalMs = hours <= 1 ? 60_000 : hours <= 24 ? 120_000 : 300_000; // 1m / 2m / 5m
        this.pollingTimer = setInterval(() => this.refreshWindow(id, hours), intervalMs);
    }

    private refreshWindow(id: string, hours: number) {
        const to = new Date();
        const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
        const existing = this.stats();
        const lastTs = existing.length > 0 ? existing[existing.length - 1].timestampUtc : null;
        // Only fetch documents newer than the last one we already have, instead of
        // re-reading the whole window every poll (avoids repeatedly re-reading the
        // same hundreds of Firestore documents on every poll tick).
        const deltaFrom = lastTs && new Date(lastTs) > from ? new Date(new Date(lastTs).getTime() + 1) : from;
        const limit = hours === 1 ? 500 : hours === 24 ? 300 : 336;
        this.api
            .getDeviceStatsRange(id, deltaFrom, to, limit)
            .then((resp) => {
                const ascNew = [...resp.items].sort((a, b) => a.timestampUtc.localeCompare(b.timestampUtc));
                const merged = lastTs ? [...existing, ...ascNew] : ascNew;
                // Trim anything that has fallen outside the sliding window
                const trimmed = merged.filter((s) => new Date(s.timestampUtc) >= from);
                this.stats.set(trimmed);
            })
            .catch((err) => console.warn('DeviceOverview: poll refresh failed', err));
    }

    private clearPolling() {
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    get device() {
        return this.deviceSig();
    }
    get p() {
        return this.device?.payload;
    }

    private clampPercent(v: number): number {
        if (isNaN(v) || !isFinite(v)) return 0;
        return Math.max(0, Math.min(100, Math.round(v)));
    }

    cpuPercent(payload: any): number {
        const v = payload?.systemMonitor?.cpu;
        const maxV = payload?.systemMonitor?.maxCpu;
        if (typeof v === 'number') {
            // Legacy 0-1 fraction: both cpu and maxCpu are <= 1
            if (v <= 1 && typeof maxV === 'number' && maxV <= 1 && maxV > 0) return this.clampPercent(v * 100);
            return this.clampPercent(v);
        }
        if (typeof v === 'string') {
            const match = v.match(/([0-9]+(?:\.[0-9]+)?)/);
            if (!match) return 0;
            return this.clampPercent(parseFloat(match[1]));
        }
        return 0;
    }
    memoryPercent(payload: any): number {
        const used = payload?.systemMonitor?.memoryUsed;
        const total = payload?.systemMonitor?.memoryTotal;
        if (typeof used === 'number' && typeof total === 'number' && total > 0) return this.clampPercent((used / total) * 100);
        return 0;
    }
    memoryText(payload: any): string {
        const used = payload?.systemMonitor?.memoryUsed;
        const total = payload?.systemMonitor?.memoryTotal;
        if (typeof used === 'number' && typeof total === 'number') return `${this.formatBytes(used)} / ${this.formatBytes(total)}`;
        return '—';
    }
    diskPercent(payload: any): number {
        const p = payload?.diskStorage?.usedPercent;
        if (typeof p === 'number') return this.clampPercent(p);
        const used = payload?.diskStorage?.usedBytes;
        const total = payload?.diskStorage?.totalBytes;
        if (typeof used === 'number' && typeof total === 'number' && total > 0) return this.clampPercent((used / total) * 100);
        return 0;
    }
    diskText(payload: any): string {
        const used = payload?.diskStorage?.usedBytes;
        const total = payload?.diskStorage?.totalBytes;
        if (typeof used === 'number' && typeof total === 'number') return `${this.formatBytes(used)} / ${this.formatBytes(total)}`;
        return '—';
    }
    formatBytes(bytes: number): string {
        if (!isFinite(bytes) || bytes < 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let i = 0;
        let v = bytes;
        while (v >= 1024 && i < units.length - 1) {
            v /= 1024;
            i++;
        }
        return `${v.toFixed(v < 10 ? 2 : 1)} ${units[i]}`;
    }
    appVersion(payload: any): string {
        return payload?.appVersion || '—';
    }
    appAssemblyVersion(payload: any): string {
        return payload?.appAssemblyVersion || '—';
    }
    firmwareVersion(payload: any): string {
        return payload?.firmwareVersion || '—';
    }
    ipAddress(payload: any): string {
        return payload?.network?.ipAddress || '—';
    }
    /** Public IP observed by the cloud at the last check-in (falls back to the geo estimate's IP for devices that haven't checked in since lastPublicIp was introduced). */
    publicIp = computed(() => {
        const d = this.deviceSig();
        return d?.lastPublicIp || d?.geoIp?.publicIp || null;
    });
    serialNumber(payload: any): string {
        return payload?.serialNumber || '—';
    }
    messages = computed(() => {
        return parseMessages(this.deviceSig()?.payload?.messages || []);
    });
    errorAndWarnings = computed(() => {
        return this.messages().filter((m) => m.level === 'error' || m.level === 'warn');
    });
    errorCount = computed(() => {
        return this.messages().filter((m) => m.level === 'error').length;
    });
    warningCount = computed(() => {
        return this.messages().filter((m) => m.level === 'warn').length;
    });
    healthBadge = computed<'success' | 'contrast' | 'secondary' | 'info' | 'danger' | 'warn' | 'success'>(() => {
        const errors = this.errorCount();
        const warnings = this.warningCount();
        if (errors > 0) return 'danger';
        if (warnings > 0) return 'warn';
        return 'success';
    });
    healthText = computed(() => {
        const errors = this.errorCount();
        const warnings = this.warningCount();
        if (errors > 0) return 'Some Issues';
        if (warnings > 0) return `${warnings} Warning${warnings !== 1 ? 's' : ''}`;
        return 'Healthy';
    });
    healthSubtext = computed(() => {
        const errors = this.errorCount();
        const warnings = this.warningCount();
        if (errors > 0) return `${errors} Error${errors !== 1 ? 's' : ''}`;
        if (warnings > 0) return 'Please check';
        return 'All systems operational';
    });
    private extractLastSeenDate(payload: any): Date | null {
        const ts = payload?.lastSeenUtc ?? (this.device as any)?.lastSeenUtc ?? this.device?.lastCheckInUtc;
        if (!ts) return null;
        try {
            return new Date(ts);
        } catch {
            return null;
        }
    }
    lastSeen(payload: any): string {
        const dt = this.extractLastSeenDate(payload);
        if (!dt) return 'Never seen';
        const now = this.now();
        const diffMs = now - dt.getTime();
        if (diffMs < 0) return 'just now';
        const sec = Math.floor(diffMs / 1000);
        if (sec < 60) return `${sec}s ago`;
        const min = Math.floor(sec / 60);
        if (min < 60) return `${min}m ago`;
        const hrs = Math.floor(min / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7) return `${days}d ago`;
        const weeks = Math.floor(days / 7);
        if (weeks < 4) return `${weeks}w ago`;
        const months = Math.floor(days / 30);
        if (months < 12) return `${months}mo ago`;
        const years = Math.floor(days / 365);
        return `${years}y ago`;
    }

    // Uptime & local time derived from payload
    private minutesSinceLastUpdate = 0;
    private lastLocalTimeChange: string | undefined;
    private tickStart = Date.now();
    uptime = computed(() => {
        const su = this.device?.payload?.uptimeSeconds ?? 0;
        const elapsedSec = Math.floor((this.now() - this.tickStart) / 1000);
        const total = su + elapsedSec;
        return moment.duration(total, 'seconds');
    });
    uptimeDetailed = computed(() => {
        const d = this.uptime();
        if (d.asMinutes() < 60) return '';
        const days = Math.floor(d.asDays());
        const hours = d.hours();
        const minutes = d.minutes();
        const parts: string[] = [];
        if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
        return parts.join(', ');
    });
    localTime = computed(() => {
        const timeString = this.device?.payload?.timeInfo?.localTime;
        if (timeString) {
            return moment(timeString, 'h:mm:ss A')
                .add(Math.floor((this.now() - this.tickStart) / 60000), 'minutes')
                .format('h:mm A');
        }
        return '';
    });
    timeZoneInfo = computed(() => {
        const tz = this.device?.payload?.timeInfo?.timeZone || '';
        const offsetInMinutes = this.device?.payload?.timeInfo?.utcOffset || 0;
        const localOffset = -new Date().getTimezoneOffset();
        const offsetDiff = offsetInMinutes - localOffset;
        if (offsetDiff === 0 && tz === 'UTC') return 'UTC';
        if (offsetDiff === 0) return tz;
        if (isNaN(offsetDiff) || !isFinite(offsetDiff) || Math.abs(offsetDiff) > 14 * 60) return tz;
        const hours = Math.floor(Math.abs(offsetDiff) / 60);
        const minutes = Math.abs(offsetDiff) % 60;
        const sign = offsetDiff >= 0 ? '+' : '-';
        const behindOrAhead = offsetDiff > 0 ? 'ahead of' : 'behind';
        if (hours === 0 && minutes === 0) return tz;
        return `${tz} ${sign}${hours}:${minutes.toString().padStart(2, '0')} ${behindOrAhead} you`;
    });
    ngOnDestroy() {
        clearInterval(this.tick);
        this.clearPolling();
    }
}

const MONITOR_EXPANDED_KEY = 'deviceOverview.monitorExpanded';

function readMonitorExpanded(): boolean {
    try {
        return localStorage.getItem(MONITOR_EXPANDED_KEY) === '1';
    } catch {
        return false;
    }
}
